/**
 * Utility functions for search data management
 * @module searchDataUtils
 */

import { getSearchKey } from './searchKey';

/**
 * Get the saved offers stored for the search matching these params
 * @param {Array} recentSearches - Recent searches from Redux
 * @param {Object} params - Search parameters
 * @returns {Array} Saved offer IDs for this search, or empty array
 */
export const getStoredSavedOffers = (recentSearches, params) => {
  if (!Array.isArray(recentSearches) || !params) return [];
  const searchKey = getSearchKey(params);
  const match = recentSearches.find(r => r.searchKey === searchKey);
  return Array.isArray(match?.savedOffers) ? match.savedOffers : [];
};

// Query keys that describe the search itself rather than the user's sort/filter choices
const NON_FILTER_KEYS = ['lang', 'cur'];

/**
 * Pick the UI filters that should be stored with a recent search
 * @param {Object} [activeFilters={}] - Filters currently applied on the results page
 * @returns {Object} Filters without search-level keys
 */
export const getFiltersToStore = (activeFilters = {}) => {
  const filters = {};
  Object.entries(activeFilters || {}).forEach(([key, value]) => {
    if (!NON_FILTER_KEYS.includes(key)) filters[key] = value;
  });
  return filters;
};

/**
 * Build standardized searchData object for saving searches
 * @param {Object} params - Search parameters
 * @param {Array} [savedOffers=[]] - Array of saved offer IDs
 * @param {Object} [filters={}] - UI filters to preserve
 * @returns {Object} Standardized searchData object
 */
export const buildSearchData = (params, savedOffers = [], filters = {}) => {
  if (!params) {
    throw new Error('buildSearchData: params is required');
  }

  return {
    searchKey: getSearchKey(params),
    params: {
      startDate: params.startDate,
      endDate: params.endDate,
      fromLocationCode: params.fromLocationCode,
      toLocationCode: params.toLocationCode,
      age: params.age,
      residency: params.residency,
      cur: params.cur
    },
    savedOffers,
    filters
  };
};